import { useCallback, useEffect, useRef, useState } from 'react'
import { isAxiosError } from 'axios'
import { cartoesApi } from '../api/endpoints'
import type { CreditCard, ErroCodigoImportacaoPdf, FaturaPdfLida, ResultadoImportacaoPdf, StatusImportacaoPdf, SugestaoCompraNoFechamento } from '../types'

/** Intervalo do polling e teto de segurança no cliente (o servidor tem o próprio timeout por leitura). */
const INTERVALO_MS = 1000
const LIMITE_MS = 60_000
const FALHAS_SEGUIDAS_MAX = 3

export interface ErroImportacaoPdf {
  mensagem: string
  /** 403 (plano sem o recurso ou cota do mês): mostrar o link para Meu plano. */
  sugerirPlano?: boolean
}

const TEXTO_POR_CODIGO: Record<ErroCodigoImportacaoPdf, string> = {
  emissor_nao_reconhecido: 'Ainda não lemos faturas desse emissor. Use o CSV.',
  pdf_ilegivel: 'Não consegui ler esse PDF. Confira se é a fatura original (com texto, não uma foto) ou use o CSV.',
  tempo_esgotado: 'A leitura demorou demais. Tente de novo em instantes.',
  erro_interno: 'Algo deu errado ao ler a fatura. Tente de novo em instantes ou use o CSV.',
}

export function ehArquivoPdf(arquivo: File): boolean {
  return arquivo.type === 'application/pdf' || arquivo.name.toLowerCase().endsWith('.pdf')
}

/** Traduz o erro do envio/consulta do PDF. O servidor já manda mensagens prontas em PT-BR (400/403); o resto ganha texto próprio. */
export function erroDeRequisicao(err: unknown): ErroImportacaoPdf {
  if (isAxiosError(err) && err.response) {
    const { status, headers } = err.response
    const msg = err.message
    if (status === 403) return { mensagem: msg, sugerirPlano: true }
    if (status === 429) {
      const seg = Number(headers?.['retry-after'])
      const espera = Number.isFinite(seg) && seg > 0 ? ` Aguarde cerca de ${Math.ceil(seg)} s.` : ''
      return { mensagem: `Muitas leituras no momento. Tente de novo em instantes.${espera}` }
    }
    if (status === 404) return { mensagem: 'Essa leitura expirou ou não foi encontrada. Envie o PDF de novo.' }
    if (status === 400) return { mensagem: msg }
  }
  return { mensagem: err instanceof Error && err.message ? err.message : 'Não foi possível ler a fatura. Tente de novo.' }
}

type Etapa = 'parado' | 'enviando' | 'lendo'

/**
 * Envia o PDF e acompanha a leitura (polling a cada ~1 s) até Pronto/Falhou.
 * Cancela sozinho ao desmontar ou ao chamar cancelar() (fechar o modal) e desiste após 60 s.
 */
export function useLeituraPdf(cartaoId: string) {
  const [etapa, setEtapa] = useState<Etapa>('parado')
  const [status, setStatus] = useState<StatusImportacaoPdf | null>(null)
  const [posicao, setPosicao] = useState<number | null>(null)
  const [erro, setErro] = useState<ErroImportacaoPdf | null>(null)
  // Cada envio ganha um número; qualquer resposta de um envio antigo/cancelado é descartada.
  const geracao = useRef(0)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  const cancelar = useCallback(() => {
    geracao.current++
    clearTimeout(timer.current)
    setEtapa('parado'); setStatus(null); setPosicao(null)
  }, [])

  useEffect(() => () => { geracao.current++; clearTimeout(timer.current) }, [])

  const iniciar = useCallback(async (arquivo: File, aoPronto: (r: ResultadoImportacaoPdf) => void) => {
    const minha = ++geracao.current
    const ativo = () => geracao.current === minha
    const falhar = (e: ErroImportacaoPdf) => { setErro(e); setEtapa('parado'); setStatus(null); setPosicao(null) }
    setErro(null); setEtapa('enviando'); setStatus(null); setPosicao(null)

    let jobId: string
    try {
      const pub = await cartoesApi.importarPdf(cartaoId, arquivo)
      if (!ativo()) return
      jobId = pub.jobId; setStatus(pub.status); setPosicao(pub.posicao ?? null); setEtapa('lendo')
    } catch (e) {
      if (ativo()) falhar(erroDeRequisicao(e))
      return
    }

    const inicio = Date.now()
    let falhasSeguidas = 0
    const consultar = async () => {
      if (!ativo()) return
      if (Date.now() - inicio > LIMITE_MS) { falhar({ mensagem: TEXTO_POR_CODIGO.tempo_esgotado }); return }
      try {
        const s = await cartoesApi.statusImportacaoPdf(cartaoId, jobId)
        if (!ativo()) return
        falhasSeguidas = 0
        if (s.status === 'Pronto' && s.resultado) { setEtapa('parado'); aoPronto(s.resultado); return }
        if (s.status === 'Falhou') {
          falhar({ mensagem: (s.erroCodigo && TEXTO_POR_CODIGO[s.erroCodigo]) || s.erro || TEXTO_POR_CODIGO.erro_interno })
          return
        }
        setStatus(s.status); setPosicao(s.posicao ?? null)
      } catch (e) {
        if (!ativo()) return
        const definitivo = isAxiosError(e) && e.response?.status === 404
        if (definitivo || ++falhasSeguidas >= FALHAS_SEGUIDAS_MAX) { falhar(erroDeRequisicao(e)); return }
      }
      timer.current = setTimeout(consultar, INTERVALO_MS)
    }
    timer.current = setTimeout(consultar, INTERVALO_MS)
  }, [cartaoId])

  return { etapa, status, posicao, erro, iniciar, cancelar, limparErro: () => setErro(null) }
}

const diaDe = (iso: string) => new Date(iso).getUTCDate()

/**
 * Sugestão do extrato sobre a compra do dia do fechamento, só quando difere do cartão atual (senão não há o que perguntar).
 * Cartão ainda não carregado = sem sugestão; campo ausente no cartão (API antiga) vale "fatura seguinte".
 */
export function mudancaFechamentoDoCartao(sugestao: SugestaoCompraNoFechamento | null | undefined, cartao?: CreditCard) {
  if (!sugestao || typeof sugestao.valor !== 'boolean' || !cartao) return null
  if (sugestao.valor === (cartao.compraNoFechamentoNaFaturaQueFecha ?? false)) return null
  return { valor: sugestao.valor, motivo: sugestao.motivo?.trim() || null }
}

/** O que o PDF traz de diferente do cadastro do cartão (só o que dá para atualizar). */
export function mudancasDoCartao(fatura: FaturaPdfLida, cartao?: CreditCard) {
  if (!cartao) return null
  const diaFechamento = diaDe(fatura.fechamento)
  const diaVencimento = diaDe(fatura.vencimento)
  const limite = fatura.limiteTotal ?? undefined
  const mudou = {
    diaFechamento: diaFechamento !== cartao.diaFechamento ? diaFechamento : undefined,
    diaVencimento: diaVencimento !== cartao.diaVencimento ? diaVencimento : undefined,
    limite: limite != null && limite !== cartao.limite ? limite : undefined,
  }
  if (mudou.diaFechamento == null && mudou.diaVencimento == null && mudou.limite == null) return null
  // `campos` é o que será enviado ao atualizar; o resto é só para mostrar no texto.
  return { campos: mudou, diaFechamento, diaVencimento, limite }
}
